/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 28/11/2023
 */

import React, { useContext, useState } from 'react';
import { useStripe, CardNumberElement, CardExpiryElement, CardCvcElement, useElements } from '@stripe/react-stripe-js';
import styles from "./PaymentForm.module.css"
import { Button, CircularProgress, Grid } from '@mui/material';
import CheckoutContext from '../../Context/CheckoutContext';
import { toast } from 'react-toastify';
import { useStripePayMutation } from '../../Store/Services/CheckoutService';
import CheckoutSuccess from './CheckoutSuccess';
import { useSelector } from 'react-redux';

const PaymentForm = () => {
  const stripe = useStripe();
  const elements = useElements()
  const [error, setError] = useState('');
  const [openSuccess, setOpenSuccess] = useState(false)

  const { checkoutData } = useContext(CheckoutContext);
  const savedAddress = useSelector(state => state.address.savedAddress);

  const cardElementOptions = {
    iconStyle: "solid",
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  const [StripePayment, {isLoading}] = useStripePayMutation();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe) {
      return;
    }

    try {
      const { token, error } = await stripe.createToken(elements.getElement(CardNumberElement));

      if (error) {
        setError(error.message);
        console.error(error);
      } else {
        const mergedData = {
          ...checkoutData,
          addressLine1: checkoutData.addressLine1 || savedAddress?.addLine1,
          addressLine2: checkoutData.addressLine2 || savedAddress?.addLine2,
          addressLine3: checkoutData.addressLine3 || savedAddress?.addLine3,
          areaId: checkoutData.areaId || savedAddress?.areaId,
          areaName: checkoutData.areaName || savedAddress?.areaName,
          stateId: checkoutData.stateId || savedAddress?.stateId,
          stateName: checkoutData.stateName || savedAddress?.stateName,
        };

        const data = {
          amount: checkoutData.amount,
          token: token.id,
          currency: process.env.REACT_APP_CURRENCY,
          req: mergedData,
        };

        try {
          const respond = await StripePayment(data).unwrap();
          setOpenSuccess(true);
        } catch (err) {
          console.error("Failed to pay: ", err);
          toast.error("Error, something went wrong!", { position: 'bottom-right' });
        }
      }
    } catch (error) {
      toast.error("An error occurred. Please try again!", { position: 'bottom-right' });
      setError('An error occurred. Please try again.');
      console.error(error);
    }
};


  return (
    <form onSubmit={handleSubmit}>
      <label>
        Card Number
        <CardNumberElement
          className={`${styles.stripeElement} ${styles.transition}`}
          options={cardElementOptions}
        />
      </label>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <label>
            Expiry Date
            <CardExpiryElement
              className={`${styles.stripeElement} ${styles.transition}`}
              options={cardElementOptions}
              />
          </label>
        </Grid>
        <Grid item xs={6}>
          <label>
            CVC
            <CardCvcElement
              className={`${styles.stripeElement} ${styles.transition}`}
              options={cardElementOptions}
            />
          </label>
        </Grid>
      </Grid>
      {/* {error && <div>{error}</div>} */}
      <Button className={styles.button} fullWidth variant='contained' type="submit" disabled={!stripe || isLoading}>
        Pay {process.env.REACT_APP_CURRENCY} {checkoutData.amount} and Confirm
        {isLoading && <CircularProgress size={24} className={styles.buttonProgress} />}
      </Button>
      <CheckoutSuccess open={openSuccess} />
    </form>
  );
};

export default PaymentForm;